import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_ENDPOINTS } from 'constants/endpoints';
import { CONSUMER_PORTAL_MODULES } from 'constants/consumer-portal';
import { api } from 'lib/api';
import type { ConsumerVolumeOverview } from 'types/admin';
import { BattleVolumeOverviewPanels, EMPTY_VOLUME_OVERVIEW } from 'components/BattleVolumeOverviewPanels';
import {
  ArrowLeft,
  Building2,
  Calendar,
  ExternalLink,
  Eye,
  EyeOff,
  Gamepad2,
  KeyRound,
  Layers,
  Lock,
  Receipt,
  RefreshCw,
  Swords,
  Users,
} from 'lucide-react';
import {
  BATTLE_STATUS_LABEL,
  BATTLE_TYPE_LABEL,
  PRIVATE_TABLE_STATUS_LABEL,
  TRANSACTION_STATUS_LABEL,
  TRANSACTION_TYPE_LABEL,
  USER_STATUS_LABEL,
  labelFor,
  statusPillByLabel,
} from 'constants/enums';
import type { ConsumerIntegration } from 'types/admin';
import { listRowNumber, LIST_TD_NO_CLASS, LIST_TH_NO_CLASS, LIST_EYE_BTN_CLASS, LIST_TABLE_CLASS, LIST_THEAD_CLASS, LIST_TBODY_CLASS, LIST_TH_CLASS, LIST_TD_CLASS } from 'lib/list-table';

const money = (symbol: string, v: unknown) =>
  `${symbol}${parseFloat(String(v ?? 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const dateTime = (v: unknown) => (v ? new Date(String(v)).toLocaleString() : '—');

const maskSecret = (secret: string) => {
  if (!secret) return '—';
  if (secret.length <= 8) return '••••••••';
  return `${secret.slice(0, 4)}${'•'.repeat(Math.min(secret.length - 8, 12))}${secret.slice(-4)}`;
};

function activityToNormalOverview(activity: Record<string, unknown>): ConsumerVolumeOverview {
  return {
    ...EMPTY_VOLUME_OVERVIEW,
    battlesPlayed: Number(activity.totalBattles) || 0,
    battlesToday: Number(activity.battlesToday) || 0,
    totalVolume: Number(activity.totalVolume) || 0,
    volumeToday: Number(activity.volumeToday) || 0,
    totalCommission: Number(activity.totalCommission) || 0,
    commissionToday: Number(activity.commissionToday) || 0,
    totalDistribute: Number(activity.totalDistributeAmount) || 0,
    distributeToday: Number(activity.distributeToday) || 0,
    commissionIsEstimated: Boolean(activity.commissionIsEstimated),
    commissionTodayIsEstimated: Boolean(activity.commissionTodayIsEstimated),
    distributeIsEstimated: Boolean(activity.distributeIsEstimated),
    distributeTodayIsEstimated: Boolean(activity.distributeTodayIsEstimated),
    houseRakePercentage: Number(activity.houseRakePercentage) || undefined,
  };
}

function activityToPrivateOverview(activity: Record<string, unknown>): ConsumerVolumeOverview {
  return {
    ...EMPTY_VOLUME_OVERVIEW,
    battlesPlayed: Number(activity.privateTotalBattles) || 0,
    battlesToday: Number(activity.privateBattlesToday) || 0,
    totalVolume: Number(activity.privateTotalVolume) || 0,
    volumeToday: Number(activity.privateVolumeToday) || 0,
    totalCommission: Number(activity.privateTotalCommission) || 0,
    commissionToday: Number(activity.privateCommissionToday) || 0,
    totalDistribute: Number(activity.privateTotalDistribute) || 0,
    distributeToday: Number(activity.privateDistributeToday) || 0,
    commissionIsEstimated: Boolean(activity.privateCommissionIsEstimated),
    commissionTodayIsEstimated: Boolean(activity.privateCommissionTodayIsEstimated),
    distributeIsEstimated: Boolean(activity.privateDistributeIsEstimated),
    distributeTodayIsEstimated: Boolean(activity.privateDistributeTodayIsEstimated),
    houseRakePercentage: Number(activity.houseRakePercentage) || undefined,
  };
}

type StatCardProps = {
  icon: React.ElementType;
  label: string;
  value: string | number;
  sub?: string;
  iconWrap: string;
};

function StatCard({ icon: Icon, label, value, sub, iconWrap, compact = false }: StatCardProps & { compact?: boolean }) {
  return (
    <div
      className={`rounded-xl border border-line p-5 shadow-sm hover:shadow-md transition-shadow ${
        compact ? 'bg-subtle/35' : 'bg-card'
      }`}
    >
      <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl mb-3 ${iconWrap}`}>
        <Icon size={20} strokeWidth={2} />
      </div>
      <p className="text-[11px] font-bold text-muted uppercase tracking-wider leading-snug">{label}</p>
      <p className="text-2xl sm:text-3xl font-extrabold text-heading mt-1 tabular-nums">{value}</p>
      {sub && <p className="text-xs text-muted mt-2 leading-snug">{sub}</p>}
    </div>
  );
}

type StatsSectionProps = {
  title: string;
  subtitle: string;
  icon: React.ElementType;
  headerBg: string;
  iconWrap: string;
  accentBar: string;
  columns?: 3 | 4;
  children: React.ReactNode;
};

function StatsSection({ title, subtitle, icon: Icon, headerBg, iconWrap, accentBar, columns = 4, children }: StatsSectionProps) {
  const gridClass =
    columns === 3
      ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'
      : 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4';
  return (
    <section className="rounded-2xl border border-line bg-card shadow-sm overflow-hidden">
      <div className={`relative px-5 py-4 sm:px-6 border-b border-line ${headerBg}`}>
        <div className={`absolute inset-y-0 left-0 w-1 ${accentBar}`} aria-hidden />
        <div className="flex items-center gap-3 pl-2">
          <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ring-1 ring-black/5 ${iconWrap}`}>
            <Icon size={20} strokeWidth={2.25} />
          </div>
          <div className="min-w-0">
            <h2 className="text-base sm:text-lg font-bold text-heading tracking-tight">{title}</h2>
            <p className="text-xs sm:text-sm text-muted mt-0.5">{subtitle}</p>
          </div>
        </div>
      </div>
      <div className={`p-5 sm:p-6 ${gridClass}`}>{children}</div>
    </section>
  );
}

export default function ConsumerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [details, setDetails] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [revealedSecret, setRevealedSecret] = useState(false);

  const fetchDetails = useCallback(() => {
    if (!id) return;
    setLoading(true);
    api
      .get(API_ENDPOINTS.ADMIN.CONSUMERS.GET_DETAILS(id))
      .then((res) => {
        if (res.success && res.data) setDetails(res.data);
      })
      .catch((err) => console.error('Failed to fetch consumer details:', err))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  if (loading && !details) {
    return (
      <div className="flex h-64 items-center justify-center text-muted font-medium">
        <RefreshCw className="animate-spin mr-2" size={20} /> Loading consumer details...
      </div>
    );
  }

  const consumer: ConsumerIntegration | null = details?.consumer ?? null;
  if (!consumer) {
    return (
      <div className="space-y-6 animate-fadeIn">
        <button
          onClick={() => navigate('/consumers')}
          className="inline-flex items-center gap-2 text-sm font-bold text-muted hover:text-heading"
        >
          <ArrowLeft size={16} /> Back to Consumers
        </button>
        <div className="bg-card p-12 rounded-2xl border border-line text-center text-muted font-medium">
          Consumer #{id} not found or could not be loaded.
        </div>
      </div>
    );
  }

  const activity = details.activitySummary ?? {};
  const symbol = consumer.currencySymbol || '';
  const assignedGames: any[] = Array.isArray(details.assignedGames) ? details.assignedGames : [];
  const recentPlayers: any[] = Array.isArray(details.recentPlayers) ? details.recentPlayers : [];
  const recentBattles: any[] = Array.isArray(details.recentBattles) ? details.recentBattles : [];
  const recentPrivateTables: any[] = Array.isArray(details.recentPrivateTables) ? details.recentPrivateTables : [];
  const recentTransactions: any[] = Array.isArray(details.recentTransactions) ? details.recentTransactions : [];

  const permissionLabels = (consumer.portalPermissions || [])
    .map((key) => CONSUMER_PORTAL_MODULES.find((m) => m.key === key)?.name || key)
    .filter(Boolean);

  const listUrl = (path: string, params: Record<string, string>) => {
    const search = new URLSearchParams(params).toString();
    return `${path}?${search}`;
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <button
        onClick={() => navigate('/consumers')}
        className="inline-flex items-center gap-2 text-sm font-semibold text-muted hover:text-heading transition-colors group"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-line bg-card group-hover:bg-subtle transition-colors">
          <ArrowLeft size={15} />
        </span>
        Back to Consumers
      </button>

      <div className="rounded-2xl border border-line bg-card shadow-sm p-5 sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-4 min-w-0">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-indigo-500/10 text-indigo-600">
              <Building2 size={24} />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <h1 className="text-xl sm:text-2xl font-bold text-heading tracking-tight">{consumer.consumerName}</h1>
                <span className="inline-flex items-center rounded-md border border-brand-gold/25 bg-brand-gold/10 px-2 py-1 font-mono text-xs font-bold text-brand-gold">
                  {consumer.consumerId}
                </span>
                <span
                  className={`px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full ${
                    consumer.status ? 'bg-emerald-500/10 text-emerald-700' : 'bg-red-500/10 text-red-700'
                  }`}
                >
                  {consumer.status ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted">
                <span>
                  Currency: <strong className="text-heading">{symbol} {consumer.defaultCurrencyCode || '—'}</strong>
                </span>
                <span className="inline-flex items-center gap-1">
                  <Calendar size={13} />
                  Created {dateTime(consumer.createdAt)}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Layers size={13} />
                  {assignedGames.length} game{assignedGames.length === 1 ? '' : 's'} assigned
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => navigate('/consumers')}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-line bg-subtle/60 text-xs font-bold text-heading hover:bg-subtle transition-colors"
            >
              Edit in list
            </button>
            <button
              type="button"
              onClick={fetchDetails}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-line text-muted hover:text-heading hover:bg-subtle transition-colors"
              title="Refresh"
            >
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-xl border border-line bg-subtle/30 p-4 space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-muted">Integration</h3>
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-[10px] font-bold uppercase text-muted">Wallet API URL</dt>
                <dd className="mt-0.5">
                  <a
                    href={consumer.thirdPartyApiUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 font-mono text-xs text-heading hover:text-brand-gold break-all"
                  >
                    {consumer.thirdPartyApiUrl}
                    <ExternalLink size={12} />
                  </a>
                </dd>
              </div>
              <div>
                <dt className="text-[10px] font-bold uppercase text-muted">Secret key</dt>
                <dd className="mt-0.5 inline-flex items-center gap-1 rounded-lg border border-line bg-card px-2 py-1.5">
                  <KeyRound size={13} className="text-faint shrink-0" />
                  <span className="font-mono text-xs text-body truncate max-w-[240px]">
                    {revealedSecret ? consumer.secretKey : maskSecret(consumer.secretKey)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setRevealedSecret((v) => !v)}
                    className="p-1 text-muted hover:text-heading rounded transition-colors"
                  >
                    {revealedSecret ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                </dd>
              </div>
            </dl>
          </div>

          <div className="rounded-xl border border-line bg-subtle/30 p-4 space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-muted">Consumer portal</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                <div>
                  <dt className="text-[10px] font-bold uppercase text-muted">Username</dt>
                  <dd className="mt-0.5 font-mono font-semibold text-heading">{consumer.portalUsername || '—'}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-bold uppercase text-muted">Password</dt>
                  <dd className="mt-0.5 font-semibold text-heading">
                    {consumer.hasPortalPassword ? 'Configured' : 'Not set'}
                  </dd>
                </div>
              </div>
              <div>
                <dt className="text-[10px] font-bold uppercase text-muted mb-1.5">Permissions</dt>
                <dd className="flex flex-wrap gap-1.5">
                  {permissionLabels.length > 0 ? (
                    permissionLabels.map((label) => (
                      <span
                        key={label}
                        className="inline-flex px-2 py-0.5 rounded-md bg-indigo-500/10 text-indigo-700 text-[10px] font-bold uppercase"
                      >
                        {label}
                      </span>
                    ))
                  ) : (
                    <span className="text-muted text-xs">No portal modules enabled</span>
                  )}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      <BattleVolumeOverviewPanels
        framed
        frameTitle="Battle overview"
        frameSubtitle="Combined totals plus public lobby and private-table breakdowns for this consumer"
        normalOverview={activityToNormalOverview(activity)}
        privateOverview={activityToPrivateOverview(activity)}
        currencySymbol={symbol}
      />

      <StatsSection
        title="Overview"
        subtitle="Players, private tables and assigned games for this consumer"
        icon={Layers}
        headerBg="bg-gradient-to-r from-indigo-500/8 via-card to-card"
        iconWrap="bg-indigo-500/12 text-indigo-600"
        accentBar="bg-indigo-500"
        columns={3}
      >
        <StatCard compact icon={Users} label="Players" value={(activity.playerCount ?? 0).toLocaleString()} sub={`${(activity.uniquePlayers ?? 0).toLocaleString()} played at least one battle`} iconWrap="bg-blue-500/10 text-blue-600" />
        <StatCard compact icon={Lock} label="Private Tables" value={(activity.privateTablesTotal ?? 0).toLocaleString()} sub={`${(activity.privateTablesActive ?? 0).toLocaleString()} active rooms`} iconWrap="bg-amber-500/10 text-amber-700" />
        <StatCard compact icon={Gamepad2} label="Assigned Games" value={(activity.gameCount ?? assignedGames.length).toLocaleString()} sub="Games this consumer can launch" iconWrap="bg-violet-500/10 text-violet-600" />
      </StatsSection>

      <div className="bg-card rounded-2xl border border-line shadow-sm overflow-hidden">
        <div className={`${LIST_TD_CLASS} border-b border-line flex items-center justify-between gap-3`}>
          <div className="flex items-center gap-2">
            <Gamepad2 size={18} className="text-violet-600" />
            <div>
              <h4 className="font-bold text-heading">Assigned games</h4>
              <p className="text-xs text-muted">Platform games enabled for this consumer</p>
            </div>
          </div>
        </div>
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {assignedGames.length > 0 ? (
            assignedGames.map((game) => (
              <div key={game.id} className="rounded-xl border border-line bg-subtle/40 p-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-heading">{game.name}</span>
                  <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-card text-brand-gold border border-line">
                    {game.gameId}
                  </span>
                </div>
                <p className="text-xs text-muted mt-2 font-mono truncate">{game.backendUrl}</p>
              </div>
            ))
          ) : (
            <p className="col-span-full text-center text-muted py-8 text-sm">No games assigned yet.</p>
          )}
        </div>
      </div>

      <div className="bg-card rounded-2xl border border-line shadow-sm overflow-hidden">
        <div className={`${LIST_TD_CLASS} border-b border-line flex items-center justify-between gap-3`}>
          <div className="flex items-center gap-2">
            <Users size={18} className="text-blue-600" />
            <div>
              <h4 className="font-bold text-heading">Recent players</h4>
              <p className="text-xs text-muted">Last 10 registered players for this consumer</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate(listUrl('/players', { consumerDbId: String(consumer.id) }))}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-line bg-subtle/60 text-xs font-bold text-heading hover:bg-subtle transition-colors shrink-0"
          >
            View all <ExternalLink size={13} />
          </button>
        </div>
        <div className="overflow-auto max-h-[50vh]">
          <table className={LIST_TABLE_CLASS}>
            <thead className={LIST_THEAD_CLASS}>
              <tr>
                <th className={`${LIST_TH_NO_CLASS}`}>No.</th>
                {['Player', 'External ID', 'Status', 'Joined', ''].map((h, i) => (
                  <th key={i} className={LIST_TH_CLASS}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={`${LIST_TBODY_CLASS} text-sm`}>
              {recentPlayers.length > 0 ? (
                recentPlayers.map((p, index) => (
                  <tr key={p.id} className="hover:bg-subtle/50 transition-colors">
                    <td className={LIST_TD_NO_CLASS}>{listRowNumber(1, index, 0)}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap font-semibold text-heading`}>
                      {p.displayName || p.username || `User #${p.id}`}
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap font-mono text-xs text-muted`}>{p.externalUserId || '—'}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full border ${statusPillByLabel(labelFor(USER_STATUS_LABEL, p.status))}`}>
                        {labelFor(USER_STATUS_LABEL, p.status)}
                      </span>
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-xs text-muted`}>{dateTime(p.createdAt)}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                      <button
                        type="button"
                        onClick={() => navigate(`/players/${p.id}/enforcement`)}
                        className={LIST_EYE_BTN_CLASS}
                        title="View player"
                        aria-label="View player"
                      >
                        <Eye size={16} strokeWidth={2} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-5 py-10 text-center text-muted font-medium">
                    No players registered for this consumer yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-card rounded-2xl border border-line shadow-sm overflow-hidden">
        <div className={`${LIST_TD_CLASS} border-b border-line flex items-center justify-between gap-3`}>
          <div className="flex items-center gap-2">
            <Swords size={18} className="text-indigo-600" />
            <div>
              <h4 className="font-bold text-heading">Recent battles</h4>
              <p className="text-xs text-muted">Last 10 battles attributed to this consumer</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate(listUrl('/battles', { consumerDbId: String(consumer.id) }))}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-line bg-subtle/60 text-xs font-bold text-heading hover:bg-subtle transition-colors shrink-0"
          >
            View all <ExternalLink size={13} />
          </button>
        </div>
        <div className="overflow-auto max-h-[50vh]">
          <table className={LIST_TABLE_CLASS}>
            <thead className={LIST_THEAD_CLASS}>
              <tr>
                <th className={`${LIST_TH_NO_CLASS}`}>No.</th>
                {['Battle', 'Type', 'Variant', 'Pot', 'Winner', 'Status', 'Played', ''].map((h, i) => (
                  <th key={i} className={LIST_TH_CLASS}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={`${LIST_TBODY_CLASS} text-sm`}>
              {recentBattles.length > 0 ? (
                recentBattles.map((b, index) => (
                  <tr key={b.id} className="hover:bg-subtle/50 transition-colors">
                    <td className={LIST_TD_NO_CLASS}>{listRowNumber(1, index, 0)}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap font-bold text-heading`}>BTL-{b.id}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>{labelFor(BATTLE_TYPE_LABEL, b.battle_type)}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-body`}>{b.variant_name || b.variant_code || '—'}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap font-bold tabular-nums`}>
                      {money(b.currency_symbol || symbol, b.pot_amount)}
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                      {b.winner_user_id ? (
                        <button
                          type="button"
                          onClick={() => navigate(`/players/${b.winner_user_id}/enforcement`)}
                          className="font-medium text-heading hover:text-brand-gold hover:underline transition-colors"
                        >
                          {b.winner_username || b.winner_display_name || `User #${b.winner_user_id}`}
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                      <span className={`px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full border ${statusPillByLabel(labelFor(BATTLE_STATUS_LABEL, b.status))}`}>
                        {labelFor(BATTLE_STATUS_LABEL, b.status)}
                      </span>
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-xs text-muted`}>{dateTime(b.created_at)}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                      <button
                        type="button"
                        onClick={() => navigate(`/battles/${b.id}`)}
                        className={LIST_EYE_BTN_CLASS}
                        title="View battle"
                        aria-label="View battle"
                      >
                        <Eye size={16} strokeWidth={2} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="px-5 py-10 text-center text-muted font-medium">
                    No battles recorded for this consumer yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-card rounded-2xl border border-line shadow-sm overflow-hidden">
          <div className={`${LIST_TD_CLASS} border-b border-line flex items-center justify-between gap-3`}>
            <div className="flex items-center gap-2">
              <Lock size={18} className="text-amber-600" />
              <div>
                <h4 className="font-bold text-heading">Recent private tables</h4>
                <p className="text-xs text-muted">Last 10 private rooms</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate(listUrl('/private-tables', { consumerDbId: String(consumer.id) }))}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-line bg-subtle/60 text-xs font-bold text-heading hover:bg-subtle transition-colors shrink-0"
            >
              View all <ExternalLink size={13} />
            </button>
          </div>
          <div className="overflow-auto max-h-[40vh]">
            <table className={LIST_TABLE_CLASS}>
              <thead className={LIST_THEAD_CLASS}>
                <tr>
                  <th className={LIST_TH_NO_CLASS}>No.</th>
                  {['Room', 'Name', 'Variant', 'Boot', 'Status', ''].map((h, i) => (
                    <th key={i} className="px-4 py-3 text-left text-xs font-bold text-muted uppercase tracking-wider bg-subtle">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className={`${LIST_TBODY_CLASS} text-sm`}>
                {recentPrivateTables.length > 0 ? (
                  recentPrivateTables.map((t, index) => (
                    <tr key={t.id} className="hover:bg-subtle/50 transition-colors">
                      <td className={`${LIST_TD_NO_CLASS} px-4 py-3`}>{listRowNumber(1, index, 0)}</td>
                      <td className="px-4 py-3 font-mono text-xs font-bold text-brand-gold">{t.room_code || '—'}</td>
                      <td className="px-4 py-3 text-heading">{t.table_name || '—'}</td>
                      <td className="px-4 py-3 text-body">{t.variant_name || t.variant_code || '—'}</td>
                      <td className="px-4 py-3 tabular-nums">{money(t.currency_symbol || symbol, t.boot_amount)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full border ${statusPillByLabel(labelFor(PRIVATE_TABLE_STATUS_LABEL, t.status))}`}>
                          {labelFor(PRIVATE_TABLE_STATUS_LABEL, t.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => navigate(`/private-tables/${t.id}`)}
                          className={LIST_EYE_BTN_CLASS}
                          title="View private table"
                          aria-label="View private table"
                        >
                          <Eye size={16} strokeWidth={2} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-muted text-sm">
                      No private tables for this consumer.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-card rounded-2xl border border-line shadow-sm overflow-hidden">
          <div className={`${LIST_TD_CLASS} border-b border-line flex items-center justify-between gap-3`}>
            <div className="flex items-center gap-2">
              <Receipt size={18} className="text-slate-600" />
              <div>
                <h4 className="font-bold text-heading">Recent transactions</h4>
                <p className="text-xs text-muted">Last 10 battle-linked ledger entries</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate(listUrl('/wallet', { consumerDbId: String(consumer.id) }))}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-line bg-subtle/60 text-xs font-bold text-heading hover:bg-subtle transition-colors shrink-0"
            >
              View all <ExternalLink size={13} />
            </button>
          </div>
          <div className="overflow-auto max-h-[40vh]">
            <table className={LIST_TABLE_CLASS}>
              <thead className={LIST_THEAD_CLASS}>
                <tr>
                  <th className={LIST_TH_NO_CLASS}>No.</th>
                  {['Txn', 'Player', 'Type', 'Amount', 'Status', 'When'].map((h, i) => (
                    <th key={i} className="px-4 py-3 text-left text-xs font-bold text-muted uppercase tracking-wider bg-subtle">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className={`${LIST_TBODY_CLASS} text-sm`}>
                {recentTransactions.length > 0 ? (
                  recentTransactions.map((t, index) => (
                    <tr key={t.id} className="hover:bg-subtle/50 transition-colors">
                      <td className={`${LIST_TD_NO_CLASS} px-4 py-3`}>{listRowNumber(1, index, 0)}</td>
                      <td className="px-4 py-3 font-mono text-xs">#{t.id}</td>
                      <td className="px-4 py-3">{t.display_name || t.username || (t.user_id ? `#${t.user_id}` : '—')}</td>
                      <td className="px-4 py-3 text-xs">
                        {labelFor(TRANSACTION_TYPE_LABEL, t.transaction_type) || t.action_code || '—'}
                      </td>
                      <td className={`px-4 py-3 tabular-nums font-semibold ${Number(t.amount) < 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                        {money(t.currency_symbol || symbol, t.amount)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full border ${statusPillByLabel(labelFor(TRANSACTION_STATUS_LABEL, t.status))}`}>
                          {labelFor(TRANSACTION_STATUS_LABEL, t.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted whitespace-nowrap">{dateTime(t.created_at)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-muted text-sm">
                      No battle transactions for this consumer.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
